import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import US_STATES from '@careerstart/wae-common/src/main/constants/US_STATES';
import { USER_EDITABLE_FIELDS } from '@careerstart/wae-common/src/main/constants/USER_EDITABLE_FIELDS';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { validateAddress } from '@careerstart/wae-common/src/main/helperFunction/profileHelperss';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import theme from '../../../theme';
import { BLACK, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';

const styles = theme?.components?.userCard;

const EditableAddressField = ({ initialValue, userId, disabled, isLoading, onAddressSave }) => {
  const [address, setAddress] = useState({
    address1: initialValue?.address1 || '',
    city: initialValue?.city || '',
    state: initialValue?.state || 'NY',
    zipcode: initialValue?.zipcode || '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only update local state when:
    // 1. initialValue is defined (not undefined/null - distinguishes from loading state)
    // 2. Not currently processing an update (to avoid race conditions)
    // 3. Not currently editing (to avoid overwriting user input)
    if (initialValue !== undefined && initialValue !== null && !isLoading && !isEditing) {
      const newAddress = {
        address1: initialValue?.address1 || '',
        city: initialValue?.city || '',
        state: initialValue?.state || 'NY',
        zipcode: initialValue?.zipcode || '',
      };
      setAddress((prevAddress) => {
        if (
          prevAddress.address1 !== newAddress.address1 ||
          prevAddress.city !== newAddress.city ||
          prevAddress.state !== newAddress.state ||
          prevAddress.zipcode !== newAddress.zipcode
        ) {
          return newAddress;
        }
        return prevAddress;
      });
    }
  }, [initialValue, isLoading, isEditing]);

  const handleAddressChange = (field) => (event) => {
    const { value } = event.target;
    setAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsEditing(true);
    setError('');
  };

  const handleAddressSave = useCallback(() => {
    const validationError = validateAddress(address);
    setError(validationError);

    if (!validationError) {
      const initialAddress = {
        address1: initialValue?.address1 || '',
        city: initialValue?.city || '',
        state: initialValue?.state || 'NY',
        zipcode: initialValue?.zipcode || '',
      };

      // Only update if the address has actually changed
      if (
        address.address1 !== initialAddress.address1 ||
        address.city !== initialAddress.city ||
        address.state !== initialAddress.state ||
        address.zipcode !== initialAddress.zipcode
      ) {
        onAddressSave({ userId, address });
      }
      setIsEditing(false);
    }
  }, [address, initialValue, userId, onAddressSave]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={styles.subTitleStyle}>
        {LanguageConverterV2('userCard.address.header')}
      </Typography>
      <TextField
        disabled={disabled}
        fullWidth
        size="small"
        value={address.address1}
        onChange={handleAddressChange(USER_EDITABLE_FIELDS.ADDRESS1)}
        placeholder={LanguageConverterV2('userCard.address.placeholder')}
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          disabled={disabled}
          fullWidth
          size="small"
          value={address.city}
          onChange={handleAddressChange(USER_EDITABLE_FIELDS.CITY)}
          placeholder={LanguageConverterV2('userCard.city.placeholder')}
          sx={{ width: '75%' }}
        />
        <Select
          disabled={disabled}
          data-testid="state-select"
          size="small"
          value={address.state}
          onChange={handleAddressChange(USER_EDITABLE_FIELDS.STATE)}
          sx={{ minWidth: 65, width: '25%' }}
        >
          {US_STATES.map((state) => (
            <MenuItem key={state.value} value={state.value}>
              {state.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField
          disabled={disabled}
          fullWidth
          size="small"
          value={address.zipcode}
          onChange={handleAddressChange(USER_EDITABLE_FIELDS.ZIP_CODE)}
          placeholder={LanguageConverterV2('userCard.zipcode.placeholder')}
          sx={{ width: '75%' }}
        />
        <Button
          variant="outlined"
          data-testid="address-save-button"
          onClick={handleAddressSave}
          disabled={!isEditing || disabled || isLoading}
          sx={{
            color: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            fontFamily: PRIMARY_FONT[500],
            borderColor: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            height: '40px',
            width: '25%',
          }}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {LanguageConverterV2('buttonText.save')}
        </Button>
      </Box>
      {error && (
        <Typography data-testid="address-error-message" sx={styles.errorMessage}>
          {LanguageConverterV2(error)}
        </Typography>
      )}
    </Box>
  );
};

EditableAddressField.propTypes = {
  initialValue: PropTypes.shape({
    address1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  userId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  onAddressSave: PropTypes.func.isRequired,
};

export default EditableAddressField;
